import { SortValue } from 'contexts/DrainContext';
import { Sku, Theme } from 'models/sku.model';

import { PurchaseMethodId } from './purchaseForm.model';
import { Category } from './Models';
import { Maybe } from './types';

export const paymentMethodMap: { [key in PurchaseMethodId]: SegmentPaymentMethod } = {
  applePay: 'apple_pay',
  googlePay: 'google_pay',
  creditCard: 'credit_card',
  paypal: 'paypal',
};

// TODO: Clean up schema of each event with data team based on unused fields in this file.

export type BaseSegmentContext = {
  server_region: string;
  store: 'consumer' | 'business';
  traits: {
    email?: string;
    phone?: string;
  };
};

export type SegmentContext = {
  protocols?: {
    event_version?: number;
  };
  device: {
    name?: string;
    type?: string;
  };
} & BaseSegmentContext;

export type CouponAppliedPayload = {
  coupon_id: string;
  coupon_name: string;
  discount: number;
  purchase_instance_id: string;
  step: number;
  step_name: string;
};

export type CouponDeniedPayload = {
  purchase_instance_id: string;
  coupon_id: string;
  reason: string;
  step: number;
  step_name: string;
};

export type CouponEnteredPayload = {
  purchase_instance_id: string;
  coupon_id: string;
  step: number;
  step_name: string;
};

export type CouponRemovedPayload = {
  purchase_instance_id: string;
  coupon_id: string;
  coupon_name: string;
  discount: number;
  step: number;
  step_name: string;
};

export type ProductViewedPayload = {
  categories?: Category['name'][];
  category: Category['name'];
  /**
   * First theme image of the Sku or empty string if no image is found.
   */
  image_url: Theme['image'];
  name: Sku['displayName'];
  nonInteraction: number;
  order_number?: string; // Unused
  order_type?: string; // Unused
  product_id: Sku['uid'];
  purchase_instance_id?: string;
  quantity: number;
  sku: Sku['slug'];
  sku_type?: string; // Unused
  ga_clientid?: Maybe<string>;
};

export type ProductAddedPayload = {
  product_id: string;
  sku: Sku['slug'];
  name: Sku['displayName'];
  quantity: number;
  price: number;
  purchase_instance_id?: string;
  step: number;
  step_name: string;
  ga_clientid?: Maybe<string>;
  url?: string;
};

export type ProductRemovedPayload = ProductAddedPayload;

export type ProductClickedPayload = {
  product_id: string;
  sku: Sku['slug'];
  categories: Category['name'][];
  category: string;
  name: string;
  quantity: number;
  image_url: string;
  position: number;
  ga_clientid?: Maybe<string>;
};

export type ProductsSearchedPayload = {
  query: string;
  search_count: number;
  products: any[];
  search_location: 'home' | 'store' | 'exchange';
  search_type: ProductSearchType;
  ga_clientid?: Maybe<string>;
};

export type CheckoutStepCompletedPayload = {
  add_recipient_method?: string;
  buy_for?: string;
  checkoutAs?: string;
  delivery_method?: string;
  element?: string;
  image_greeting_card?: string;
  is_recipient_email_same?: boolean;
  journey?: string;
  order_id?: string;
  order_number?: string;
  order_type?: string;
  purchase_instance_id?: string;
  reci_contact_shared?: string;
  step: number;
  step_name: string;
  wrap_has_card_design?: boolean;
  wrap_has_gif?: boolean;
  wrap_has_greeting_card?: boolean;
  wrap_has_message?: boolean;
  wrap_has_av?: boolean;
  recipient_count?: number;
  price?: number;
  has_promo?: boolean;
  has_previewed?: boolean;
  payment_method?: SegmentPaymentMethod;
};

export type CustomFilterSortAppliedPayload = {
  has_filter?: boolean;
  has_audience_filter?: boolean;
  has_interests_filter?: boolean;
  has_location_filter?: boolean;
  has_occasion_filter?: boolean;
  has_search?: boolean;
  has_sort?: boolean;
  audience_filter_tags?: string;
  current_sort_select?: SortValue;
  interests_filter_tags?: string;
  location_filter_tags?: string;
  occasion_filter_tags?: string;
  previous_sort_select?: SortValue;
  purchase_instance_id?: string;
};

export type CustomElementInteractionPayload = {
  checkbox_message?: string;
  checkbox_selection?: string;
  custom_feature?: string;
  element: string;
  error_message?: string;
  image_greeting_card?: string;
  journey?: string;
  order_number?: string;
  order_type?: string;
  page_location?: string;
  pagination?: string;
  path?: string;
  payment_method?: string;
  popup_exit_method?: string;
  previous_step?: string;
  products?: [
    {
      categories: Category['name'][];
      category?: Category['name'];
      name?: Sku['displayName'];
      product_id?: Sku['uid'];
      sku?: Sku['slug'];
      url?: string;
    }
  ];
  reminder_id?: string;
  reminder_instance_id?: string;
  purchase_instance_id?: string;
  signup_instance_id?: string;
  signup_type?: string;
  step?: number;
  step_name?: string;
  subject?: string;
  template_option?: string;
  user_type?: string;
  value?: number;
  ga_clientid?: Maybe<string>;
  element_href?: string;
};

export type CheckoutStepViewedPayload = {
  navigate_option?: string;
  order_id?: object;
  order_number?: string;
  order_started_from?: string;
  order_type?: string;
  previous_step?: string;
  purchase_instance_id?: string;
  step: number;
  step_name: string;
};

export type SkuSegmentPayload = {
  product_id: string;
  sku: string;
  categories?: string[];
  category?: string;
  name: string;
  image_url?: string;
  url?: string;
  gift_theme?: string;
  price: number;
  is_custom_sku_theme_design?: boolean;
  quantity: number;
};

export type SegmentPaymentMethod = 'apple_pay' | 'credit_card' | 'google_pay' | 'paypal';

export type OrderCompletedPayload = {
  coupon?: string;
  buy_for: string;
  video_message: boolean;
  delivery_method: string;
  purchase_instance_id: string;
  order_number: string;
  order_id: string;
  total: number;
  currency: string;
  is_zag_card: boolean;
  products: SkuSegmentPayload[];
  payment_method: SegmentPaymentMethod;
  recipient_count: number;
  has_card_design: boolean;
  card_design_link?: string;
  has_greeting_card: boolean;
  greeting_card_link?: string;
  has_gif: boolean;
  giphy_id?: string;
  has_video_or_audio: boolean;
  has_message: boolean;
  ttclid?: Maybe<string>;
  ga_clientid?: Maybe<string>;
};

export type OrderFailedPayload = {
  affiliation: string;
  buy_for: string;
  coupon: string;
  currency: string;
  delivery_method: string;
  fail_reason: string;
  order_number: string;
  purchase_instance_id: string;
  total: number;
  video_message: boolean;
  products: SkuSegmentPayload[];
  recipient_count: number;
};

export type GifSearchedPayload = {
  query: string;
  search_count?: number;
  step: number;
  step_name: string;
  purchase_instance_id: string;
};

export enum ProductSearchType {
  Keyword = 'keyword',
  Filter = 'filter',
  Both = 'both',
}
export type SignedInPayload = {
  signin_type?: string;
  username: string;
  ga_clientid?: Maybe<string>;
};

export type SignedUpPayload = {
  created_at?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  signup_instance_id?: string;
  signup_type?: string;
  username?: string;
  server_region?: string;
  user_type?: string;
  ga_clientid?: Maybe<string>;
};

export type BusinessLeadSubmitted = {
  business_name: string;
  email: string;
  first_name: string;
  last_name: string;
  lead_type: string;
  phone: string;
  store: string;
  ga_clientid?: Maybe<string>;
};
